import React, { ChangeEvent, ReactNode, useCallback, useState } from "react"
import styles from "./SearchInput.module.scss"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch, faTimes } from "@fortawesome/pro-light-svg-icons"
import { FormControl } from "react-bootstrap"
import { formTranslation } from "../../locales/form"
import { useTranslation } from "react-i18next"
import { ClassProps } from "../../utility/common/props"
import cn from "classnames"
import { testId } from "../../utility/tests/testId/common"
import debounce from "lodash/debounce"
import Spinner from "../Spinner/Spinner"

export interface SearchInputProps extends ClassProps {
    onChange: (searchValue: string) => void
    disabled?: boolean
    label?: string
    isLoading?: boolean
    placeholder?: string
    inputId?: string
    name?: string
    onFocus?: () => void
    onBlur?: () => void
    delay?: number
    dataTestId?: string
    clearButtonDataTestId?: string
    isHideClearBtn?: boolean
    rightAddon?: ReactNode
}

const SearchInput: React.FC<SearchInputProps> = props => {
    const { t } = useTranslation()
    const {
        onChange,
        className,
        disabled,
        label,
        isLoading = false,
        placeholder,
        inputId,
        name,
        onFocus,
        onBlur,
        delay = 300,
        dataTestId = testId.searchInput,
        clearButtonDataTestId = testId.searchInputClearButton,
        isHideClearBtn = false,
        rightAddon
    } = props
    const [searchValue, setSearchValue] = useState("")

    const handleChangeCallback = useCallback(
        debounce((searchValue: string) => {
            onChange(searchValue)
        }, delay),
        []
    )

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value)
        handleChangeCallback(e.target.value)
    }

    const handleClear = () => {
        setSearchValue("")
        handleChangeCallback("")
    }
    const inputLabel = label || placeholder || t(formTranslation.search)

    return (
        <div className={cn(styles.wrap, className)}>
            <div className={cn(styles.left)}>
                <FontAwesomeIcon icon={faSearch} className={cn(styles.left__icon)} />
                {label && <span>{label}</span>}
            </div>
            <FormControl
                id={inputId}
                name={name}
                onChange={handleChange}
                value={searchValue}
                className={cn(styles.input, label && styles.input_withLabel, rightAddon && styles.input_withAddon)}
                placeholder={placeholder || t(formTranslation.search)}
                aria-label={inputLabel}
                disabled={disabled}
                data-testid={dataTestId}
                onFocus={onFocus}
                onBlur={onBlur}
            />
            {rightAddon ? (
                <div className={cn(styles.right, styles.right_withAddon)}>
                    {!isHideClearBtn && (
                        <div
                            className={cn(styles.right__clear, !isLoading && styles.right_clear)}
                            onClick={isLoading ? undefined : handleClear}
                            data-testid={clearButtonDataTestId}
                        >
                            {isLoading ? (
                                <Spinner className={styles.right__loading} />
                            ) : (
                                <FontAwesomeIcon icon={faTimes} className={styles.right__icon} />
                            )}
                        </div>
                    )}
                    <div className={styles.right__addon}>{rightAddon}</div>
                </div>
            ) : (
                !isHideClearBtn && (
                    <div
                        className={cn(styles.right, !isLoading && styles.right_clear)}
                        onClick={isLoading ? undefined : handleClear}
                        data-testid={clearButtonDataTestId}
                    >
                        {isLoading ? (
                            <Spinner className={styles.right__loading} />
                        ) : (
                            <FontAwesomeIcon icon={faTimes} className={styles.right__icon} />
                        )}
                    </div>
                )
            )}
        </div>
    )
}

export default SearchInput
